import { useSearchParams } from "react-router-dom";
import { useEffect, useRef } from "react";
import GaugeChart from "react-gauge-chart";
import {
  trackResultsPageLoaded,
  trackRiskScoreDisplayed,
} from "../utils/rum-tracking";

// Card component to replace Flowbite Card
const Card = ({ children, className = "" }: { children: React.ReactNode; className?: string }) => (
  <div className={`bg-gray-800  rounded-lg shadow-lg p-6 ${className}`}>
    {children}
  </div>
);

export default function ResultsPage() {
  const [searchParams] = useSearchParams();
  
  // Extract query parameters
  let assessment_id = searchParams.get("assessment_id");
  let first_name = searchParams.get("first_name");
  let last_name = searchParams.get("last_name");
  let email = searchParams.get("email");
  let phone = searchParams.get("phone");
  let sax_id = searchParams.get("sax_id");
  let risk_score = searchParams.get("risk_score");
  let tenant_id = searchParams.get("tenant_id");
  let practice_id = searchParams.get("practice_id");

  // Add default test data only in development mode
  if (process.env.NODE_ENV === 'development') {
    // Only use defaults if no parameters are provided
    if (!assessment_id) assessment_id = "test_assessment_123";
    if (!first_name) first_name = "John";
    if (!last_name) last_name = "Doe";
    if (!email) email = "john.doe@example.com";
    if (!phone) phone = "+1-555-123-4567";
    if (!sax_id) sax_id = "SAX_TEST_001";
    if (!risk_score) risk_score = "7"; // Moderate risk for testing
    if (!tenant_id) tenant_id = "test_tenant_456";
    if (!practice_id) practice_id = "test_practice_789";
  }

  // Track results page loaded and risk score displayed (only once per assessment_id)
  const lastTrackedAssessmentId = useRef<string | null>(null);
  
  useEffect(() => {
    const hasCompleteData = assessment_id && practice_id && tenant_id && sax_id && risk_score;
    const isNewAssessment = lastTrackedAssessmentId.current !== assessment_id;

    if (hasCompleteData && isNewAssessment) {
      lastTrackedAssessmentId.current = assessment_id;

      trackResultsPageLoaded({
        assessmentId: assessment_id || undefined,
        saxId: sax_id || undefined,
        practiceId: practice_id || undefined,
        tenantId: tenant_id || undefined,
      });

      const scoreValue = risk_score || "0";
      const scoreNum = parseFloat(scoreValue);
      trackRiskScoreDisplayed(scoreValue, {
        assessmentId: assessment_id || undefined,
        saxId: sax_id || undefined,
        practiceId: practice_id || undefined,
        tenantId: tenant_id || undefined,
        riskLevel: scoreNum >= 10 ? "high" : scoreNum >= 6 ? "moderate" : "low",
      });
    }
  }, [assessment_id, practice_id, tenant_id, sax_id, risk_score]);
  // Log unused variables for debugging (can be removed in production)
  console.log("Assessment ID:", assessment_id);
  console.log("Tenant ID:", tenant_id);
  console.log("Practice ID:", practice_id);

  // Create the Zoho bookings iframe URL with the extracted parameters
  const prefilledIframeUrl = `https://booknow.sleeparchitx.com/portal-embed#/3985874000009181114?Name=${encodeURIComponent((first_name || "") + " " + (last_name || ""))}&Email=${encodeURIComponent(email || "")}&Contact%20Number=${encodeURIComponent(phone || "")}&Notes=${encodeURIComponent(sax_id || "")}&customer_first_name=${encodeURIComponent(sax_id || "")}`;
  
  return (
    <div 
      className="w-full min-h-screen bg-center bg-no-repeat bg-cover"
      style={{
        backgroundImage: "url('/images/bg7.png')",
      }}
    >
      <div className="flex flex-col w-full min-h-screen p-4">
        {/* Top half - Gauge and text area */}
        <div className="w-full max-w-6xl mx-auto mb-8 bg-gray-800">
          <Card className="bg-gray-800" >
            <div className="text-center bg-gray-800 ">
              {/* Risk Score Gauge */}
              <div className="mb-8 bg-gray-800 ">
                <div style={{ width: '28.125%', margin: '0 auto' }}>
                  <GaugeChart
                    id="risk-score-gauge"
                    nrOfLevels={3}
                    percent={risk_score ? Math.min(parseFloat(risk_score) / 10, 1) : 0}
                    arcWidth={0.3}
                    colors={["#16A34A", "#EAB308", "#DC2626"]}
                    textColor="#374151"
                    needleColor="#6B7280"
                    needleBaseColor="#6B7280"
                    hideText={true}
                    arcPadding={0.02}
                  />
                </div>
                <div className="mt-4">
                  <h3 className="mb-2 text-xl font-semibold text-white">
                    Risk Score: {risk_score || 0}
                  </h3>
                  <div className="flex justify-center space-x-4 text-sm text-white">
                    <span className="flex items-center">
                      <div className="w-3 h-3 mr-1 bg-green-500 rounded-full"></div>
                      Low Risk (0-5)
                    </span>
                    <span className="flex items-center">
                      <div className="w-3 h-3 mr-1 bg-yellow-500 rounded-full"></div>
                      Moderate Risk (6-9)
                    </span>
                    <span className="flex items-center">
                      <div className="w-3 h-3 mr-1 bg-red-500 rounded-full"></div>
                      High Risk (10+)
                    </span>
                  </div>
                  
                  {/* Conditional text for different risk levels */}
                  {risk_score && parseInt(risk_score) >= 1 && parseInt(risk_score) <= 3 && (
                    <div className="p-4 mt-6 bg-green-600 rounded-lg">
                      <p className="leading-relaxed text-center text-white">
                        Your sleep health answers suggest you are not at risk for problems related to unhealthy sleep.
                      </p>
                    </div>
                  )}
                  {risk_score && parseInt(risk_score) >= 4 && (
                    <div className="p-4 mt-6 bg-gray-800 rounded-lg">
                      <p className="leading-relaxed text-center text-white">
                        Your sleep health answers suggest you are at risk for problems related to unhealthy sleep. 
                        Your doctor recommends you schedule a Free, 5-minute call using the calendar below to learn more about your sleep health.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </Card>
        </div>

        {/* Bottom half - Zoho bookings iframe */}
        <div className="flex-1 w-full max-w-6xl mx-auto bg-gray-800">
          <Card className="h-full bg-gray-800 bg-opacity-95 backdrop-blur-sm">
            <div className="bg-gray-800  w-full h-[500px] sm:h-[600px]">
              <iframe
                width="100%"
                height="100%"
                src={prefilledIframeUrl}
                frameBorder="0"
                allowFullScreen
                title="Booking Portal"
                className="w-full h-full rounded-lg"
              />
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
} 